import { type CyberAssessmentSelectDto, type TechnologySelectDto } from '@@types/api.types';
import { DropdownMenuComponent, type DropdownMenuItemType } from '@components/core/dropdown-menu.component';
import { IconButton } from '@components/core/icon-button.component';
import {
  cyberScoreTileOverviewGridClassName,
  formatTrlBand,
} from '@components/pages/technology/components/cyber-score-tile.util';
import { CyberScore } from '@components/pages/technology/components/cyber/cyber-score.component';
import { BadgeList } from '@components/shared/badge-list.component';
import { TextWithLabel } from '@components/shared/text-with-label.component';
import { Tile } from '@components/shared/tile.component';
import { formatDate } from '@utils/date.util';
import { EllipsisVertical } from 'lucide-react';

type CyberScoreTileProps = {
  assessment: CyberAssessmentSelectDto;
  canManageAssessments?: boolean;
  cpiPercentage: number;
  onReviewAnswers: () => void;
  onStartNewAssessment: () => void;
  technology: TechnologySelectDto;
};

const getUseBadgeItems = (technology: TechnologySelectDto): { id: string; name: string }[] => {
  return technology.uses.map((use) => ({ id: use.id, name: use.name }));
};

const getDomainBadgeItems = (technology: TechnologySelectDto): { id: string; name: string }[] => {
  const allDomains = technology.uses.flatMap((use) => use.domains);
  const uniqueDomains = [...new Map(allDomains.map((domain) => [domain.id, domain])).values()];

  return uniqueDomains.map((domain) => ({ id: domain.id, name: domain.name }));
};

export const CyberScoreTile: React.FC<CyberScoreTileProps> = (props) => {
  const {
    assessment,
    canManageAssessments = true,
    cpiPercentage,
    onReviewAnswers,
    onStartNewAssessment,
    technology,
  } = props;
  const overview = assessment.reportSnapshot?.overview;
  const domainBadgeItems = getDomainBadgeItems(technology);
  const useBadgeItems = getUseBadgeItems(technology);

  const menuItems: DropdownMenuItemType[] = canManageAssessments
    ? [
        { onClick: onReviewAnswers, text: 'Review Answers' },
        { onClick: onStartNewAssessment, text: 'Start New Assessment' },
      ]
    : [];

  return (
    <Tile
      className="border border-gray-200 shadow-none"
      hoverReveal
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <CyberScore
            cpiBand={overview?.cpiBand}
            cpiBandLabel={overview?.cpiBandLabel}
            cpiPercentage={cpiPercentage}
            tooltipContent={
              <div className="flex max-w-xs flex-col gap-1">
                <div className="font-semibold text-teal-600">CPI Score: {cpiPercentage}%</div>
                <div className="text-sm">
                  The Cyber Posture Index (CPI) measures overall cybersecurity readiness as a ratio of favorable
                  responses to total applicable questions.
                </div>
              </div>
            }
          />

          {menuItems.length > 0 ? (
            <DropdownMenuComponent
              items={menuItems}
              trigger={
                <IconButton.HoverReveal
                  ariaLabel="Open actions menu"
                  icon={<EllipsisVertical />}
                  variant="secondary"
                />
              }
            />
          ) : null}
        </div>

        <div className="rounded-xl bg-slate-50 p-4">
          <div className={cyberScoreTileOverviewGridClassName}>
            <TextWithLabel
              className="flex-col-reverse text-sm"
              label="CPI Ratio"
              labelClassName="text-xs"
              title={(overview?.cpiRatio ?? 0).toFixed(3)}
            />
            <TextWithLabel
              className="flex-col-reverse text-sm"
              label="TRL Band"
              labelClassName="text-xs"
              title={formatTrlBand(assessment.trlBand)}
            />
            <TextWithLabel
              className="flex-col-reverse text-sm"
              label="Use"
              labelClassName="text-xs"
              title={
                useBadgeItems.length > 0 ? (
                  <BadgeList
                    items={useBadgeItems}
                    limit={2}
                    size="xs"
                  />
                ) : (
                  'N/A'
                )
              }
            />
            <TextWithLabel
              className="flex-col-reverse text-sm"
              label="Domain"
              labelClassName="text-xs"
              title={
                domainBadgeItems.length > 0 ? (
                  <BadgeList
                    items={domainBadgeItems}
                    limit={2}
                    size="xs"
                  />
                ) : (
                  'N/A'
                )
              }
            />
            <TextWithLabel
              className="flex-col-reverse text-sm"
              label="Assessment Date"
              labelClassName="text-xs"
              title={formatDate(assessment.createdAt)}
            />
            <TextWithLabel
              className="flex-col-reverse text-sm"
              label="Questions Answered"
              labelClassName="text-xs"
              title={`${overview?.answeredQuestions ?? 0}/${overview?.totalQuestions ?? 0}`}
            />
          </div>
        </div>
      </div>
    </Tile>
  );
};
